<template>
  <div
    v-if="targetedCustomEventsByCategory.length > 0"
    class="row">
    <div class="col-12">
      <div class="pane-header">
        <span class="icon"><icon-colored-event /></span>
        <span class="text">{{ capitalize($t('messages.customEventTargeting')) }}</span>
      </div>
    </div>
    <div class="col-12">
      <div class="pane-content pad-top">
        <div class="row">
          <div class="col-12">
            <table
              class="table"
              :aria-label="i18n.global.t('names.customEvent', { n: 2 })">
              <thead>
                <tr>
                  <th />
                  <th>{{ capitalize($t('names.category', { n: 1 })) }}</th>
                  <th>{{ capitalize($t('names.event', { n: 2 })) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(cat, i) in targetedCustomEventsByCategory"
                  :key="cat.categoryName">
                  <td>
                    <span
                      v-show="i > 0"
                      class="badge badge-sm badge-default-hollow"
                      >{{ uppercase($t('conjuctions.and')) }}</span
                    >
                  </td>
                  <td>{{ cat.categoryName }}</td>
                  <td>
                    <template
                      v-for="(val, j) in cat.values"
                      :key="j">
                      <span>
                        <span
                          v-if="j > 0"
                          class="ml-2 mr-2 text-muted"
                          >{{ uppercase($t('conjuctions.or')) }}</span
                        >
                        <span class="badge badge-info-hollow">{{ val.name }}</span>
                      </span>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed } from 'vue';

  import { IconColoredEvent } from '@/common/icons/colored';
  import { Package, TargetingCriterion } from '@/common/models';
  import { uppercase } from '@/globals';
  import i18n from '@/vueI18n';

  interface IDealDetailsCustomEventTargetingProps {
    deal: Package;
    targetingCriteria: TargetingCriterion[];
  }

  const props = defineProps<IDealDetailsCustomEventTargetingProps>();

  const targetedCustomEvents = computed(() => {
    if (
      props.targetingCriteria == null ||
      props.targetingCriteria.length === 0 ||
      props.deal?.packageTargetingCriteria == null
    ) {
      return [];
    }
    const externalPushedTargetCriteria = props.targetingCriteria.filter((x) => x.isUpdatePushedExternally);
    if (externalPushedTargetCriteria.length > 0) {
      const allSubTypes = externalPushedTargetCriteria.map((x) => x.subTypes).flat();
      const packageTargetCriteria = props.deal.packageTargetingCriteria.filter((x) =>
        externalPushedTargetCriteria.some((ec) => ec.id === x.targetingCriterionID),
      );
      return packageTargetCriteria
        .map((x) => x.values)
        .flat()
        .map((x) => allSubTypes.find((y) => y.code === x.code));
    }
    return [];
  });

  interface ICustomEventCategory {
    categoryName: string;
    values: TargetingCriterion[];
  }

  const targetedCustomEventsByCategory = computed<ICustomEventCategory>(() =>
    targetedCustomEvents.value.reduce((mem, ce) => {
      const existingCategory = mem.find((x) => x.categoryName === ce.categoryName);
      if (existingCategory != null) {
        return [
          ...mem.filter((x) => x !== existingCategory),
          { categoryName: existingCategory.categoryName, values: [...existingCategory.values, ce] },
        ];
      }
      return [...mem, { categoryName: ce.categoryName, values: [ce] }];
    }, []),
  );
</script>

<style lang="scss" scoped>
  @use '@/styles/variables.scss' as *;
</style>
